import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Save,
  Send,
  Eye,
  Loader2,
  FileText,
  Bot,
  Code,
  BookOpen
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminBlogEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [blog, setBlog] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    author: 'Jigsaw Conferences Team',
    sectorTags: [],
    city: '',
    status: 'draft',
    metaTitle: '',
    metaDescription: '',
    featuredImage: ''
  });
  const [tagsInput, setTagsInput] = useState('');

  useEffect(() => {
    if (!isNew) {
      fetchBlog();
    }
  }, [id, isNew]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/blogs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlog(response.data);
      setTagsInput(response.data.sectorTags?.join(', ') || '');
    } catch (error) {
      toast.error('Failed to load blog post');
      navigate('/admin/blogs');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .trim('-');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setBlog(prev => ({
      ...prev,
      title,
      slug: prev.slug || generateSlug(title),
      metaTitle: prev.metaTitle || title
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('admin_token');
      
      const payload = {
        ...blog,
        sectorTags: tagsInput.split(',').map(t => t.trim()).filter(Boolean)
      };

      if (isNew) {
        const response = await axios.post(
          `${BACKEND_URL}/api/admin/blogs`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Blog post created!');
        navigate(`/admin/blogs/${response.data.blog.id}`);
      } else {
        await axios.put(
          `${BACKEND_URL}/api/admin/blogs/${id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Blog post saved!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save blog post');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    try {
      setPublishing(true);
      const token = localStorage.getItem('admin_token');
      
      // Save first if new
      if (isNew) {
        const response = await axios.post(
          `${BACKEND_URL}/api/admin/blogs`,
          {
            ...blog,
            sectorTags: tagsInput.split(',').map(t => t.trim()).filter(Boolean)
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Then publish
        await axios.post(
          `${BACKEND_URL}/api/admin/blogs/${response.data.blog.id}/publish`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Blog post published!');
        navigate('/admin/blogs');
      } else {
        // Save then publish
        await handleSave();
        await axios.post(
          `${BACKEND_URL}/api/admin/blogs/${id}/publish`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Blog post published!');
        setBlog(prev => ({ ...prev, status: 'published' }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen flex justify-center items-center">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <Link to="/admin/blogs" className="text-sm text-gray-500 hover:text-gray-700 flex items-center mb-2">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to Blogs
              </Link>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <FileText className="w-6 h-6 mr-2 text-orange-600" />
                {isNew ? 'New Blog Post' : 'Edit Blog Post'}
              </h1>
            </div>
            <div className="flex items-center gap-3">
              {blog.generatedBy === 'auto' && (
                <Badge variant="outline" className="text-purple-600 border-purple-300">
                  <Bot className="w-3 h-3 mr-1" />
                  AI Generated
                </Badge>
              )}
              <Button
                variant="outline"
                onClick={() => setShowPreview(!showPreview)}
              >
                {showPreview ? <Code className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                {showPreview ? 'Edit' : 'Preview'}
              </Button>
              <Button
                variant="outline"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Draft
              </Button>
              {blog.status !== 'published' && (
                <Button
                  onClick={handlePublish}
                  disabled={publishing}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Publish
                </Button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {showPreview ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <BookOpen className="w-5 h-5 mr-2" />
                      Preview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <h1 className="text-3xl font-bold mb-4">{blog.title || 'Untitled'}</h1>
                    <p className="text-gray-600 mb-6 italic">{blog.excerpt}</p>
                    <div 
                      className="prose max-w-none"
                      dangerouslySetInnerHTML={{ __html: blog.content }}
                    />
                  </CardContent>
                </Card>
              ) : (
                <>
                  <Card>
                    <CardContent className="p-6 space-y-4">
                      <div>
                        <Label htmlFor="title">Title *</Label>
                        <Input
                          id="title"
                          value={blog.title}
                          onChange={handleTitleChange}
                          placeholder="Enter blog title..."
                          className="text-lg font-semibold"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="slug">URL Slug</Label>
                        <div className="flex items-center">
                          <span className="text-sm text-gray-500 mr-2">/blog/</span>
                          <Input
                            id="slug"
                            value={blog.slug}
                            onChange={(e) => setBlog({ ...blog, slug: e.target.value })}
                            placeholder="url-slug"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="excerpt">Excerpt / Summary *</Label>
                        <Textarea
                          id="excerpt"
                          value={blog.excerpt}
                          onChange={(e) => setBlog({ ...blog, excerpt: e.target.value })}
                          placeholder="Brief summary for listings and SEO..."
                          rows={3}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Content (HTML)</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Textarea
                        value={blog.content}
                        onChange={(e) => setBlog({ ...blog, content: e.target.value })}
                        placeholder="<p>Your blog content here...</p>"
                        rows={20}
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-2">
                        Supports HTML formatting. Use &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;a&gt; tags.
                      </p>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Post Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={blog.status}
                      onValueChange={(value) => setBlog({ ...blog, status: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="published">Published</SelectItem>
                        <SelectItem value="scheduled">Scheduled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="author">Author</Label>
                    <Input
                      id="author"
                      value={blog.author}
                      onChange={(e) => setBlog({ ...blog, author: e.target.value })}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="tags">Sector Tags</Label>
                    <Input
                      id="tags"
                      value={tagsInput}
                      onChange={(e) => setTagsInput(e.target.value)}
                      placeholder="Technology, Healthcare, Events"
                    />
                    <p className="text-xs text-gray-500 mt-1">Comma-separated</p>
                  </div>
                  
                  <div>
                    <Label htmlFor="city">City (slug)</Label>
                    <Input
                      id="city"
                      value={blog.city || ''}
                      onChange={(e) => setBlog({ ...blog, city: e.target.value })}
                      placeholder="london"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">SEO Settings</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="metaTitle">Meta Title</Label>
                    <Input
                      id="metaTitle"
                      value={blog.metaTitle || ''}
                      onChange={(e) => setBlog({ ...blog, metaTitle: e.target.value })}
                      placeholder="SEO optimized title..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {(blog.metaTitle || '').length}/60 characters
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="metaDescription">Meta Description</Label>
                    <Textarea
                      id="metaDescription"
                      value={blog.metaDescription || ''}
                      onChange={(e) => setBlog({ ...blog, metaDescription: e.target.value })}
                      placeholder="SEO description..."
                      rows={3}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {(blog.metaDescription || '').length}/160 characters
                    </p>
                  </div>
                  
                  <div>
                    <Label htmlFor="featuredImage">Featured Image URL</Label>
                    <Input
                      id="featuredImage"
                      value={blog.featuredImage || ''}
                      onChange={(e) => setBlog({ ...blog, featuredImage: e.target.value })}
                      placeholder="https://..."
                    />
                  </div>
                </CardContent>
              </Card>

              {blog.createdAt && (
                <Card>
                  <CardContent className="p-4 text-sm text-gray-500">
                    <p>Created: {new Date(blog.createdAt).toLocaleString()}</p>
                    <p>Updated: {new Date(blog.updatedAt).toLocaleString()}</p>
                    {blog.publishDate && (
                      <p>Published: {new Date(blog.publishDate).toLocaleString()}</p>
                    )}
                    <p>Views: {blog.viewCount || 0}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
